import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enTranslations from './locales/en/translation.json';
import heTranslations from './locales/he/translation.json';

// Configure language detection
const languageDetector = new LanguageDetector();

// Custom detector for location-based language detection
// This is used for NEW users (before they have a database preference)
// For logged-in users, language is loaded from database in AppRouter
languageDetector.addDetector({
  name: 'locationBased',
  lookup() {
    try {
      // Skip location-based detection if user has a stored preference
      // (This prevents overriding database preference on page reload)
      const stored = localStorage.getItem('i18nextLng');
      if (stored && ['en', 'he'].includes(stored)) {
        return null; // Let userPreference detector handle it
      }
      
      // Method 1: Check timezone FIRST (most reliable for location-based detection)
      // This ensures Israeli users see Hebrew even if their browser is in English
      // CRITICAL: Timezone check must come before browser language to prioritize location over browser settings
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timezone === 'Asia/Jerusalem') {
          return 'he'; // Israeli users should see Hebrew regardless of browser language
        }
      } catch (e) {
        // Timezone detection failed, continue to other methods
      }
      
      // Method 2: Check browser language (secondary indicator)
      // Only used if timezone doesn't indicate Israel
      const browserLang = navigator.language || navigator.userLanguage;
      if (browserLang) {
        // Check if Hebrew (he, he-IL, etc.) - Israeli users
        if (browserLang.toLowerCase().startsWith('he')) {
          return 'he';
        }
        // Check if English (en, en-US, en-GB, etc.) - non-Israeli users
        // NOTE: This will only apply if timezone is NOT Asia/Jerusalem
        if (browserLang.toLowerCase().startsWith('en')) {
          return 'en';
        }
      }
      
      // Method 3: Check locale (fallback)
      try {
        const locale = Intl.DateTimeFormat().resolvedOptions().locale;
        if (locale && locale.toLowerCase().startsWith('he')) {
          return 'he';
        }
      } catch (e) {
        // Locale detection failed, continue
      }
      
      return null; // No location-based match found - will fallback to 'en'
    } catch (e) {
      // If any detection fails, return null (fallback to default 'en')
      return null;
    }
  },
  cacheUserLanguage(lng) {
    // Don't cache location-based detection automatically
    // Only cache when explicitly set by user or loaded from database
  }
});

languageDetector.addDetector({
  name: 'userPreference',
  lookup() {
    // For logged-in users, we'll load from database in AppRouter
    // For now, check localStorage (will be updated from database if user is logged in)
    const stored = localStorage.getItem('i18nextLng');
    if (stored && ['en', 'he'].includes(stored)) {
      return stored;
    }
    
    // No stored preference - will be set by location-based detection or database
    return null;
  },
  cacheUserLanguage(lng) {
    // Only cache valid language codes
    if (lng && ['en', 'he'].includes(lng)) {
      localStorage.setItem('i18nextLng', lng);
    }
  }
});

// Set document direction based on language
const updateDocumentDirection = (lng) => {
  if (!lng) return;
  const isRTL = lng === 'he';
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  document.documentElement.lang = lng;
};

// Export for use in React components that need to ensure RTL is set
export const ensureCorrectDirection = () => {
  if (i18n.isInitialized && i18n.language) {
    updateDocumentDirection(i18n.language);
  }
};

// Update direction when language changes
i18n.on('languageChanged', (lng) => {
  updateDocumentDirection(lng);
});

// Also listen for initialized event to catch early language detection
i18n.on('initialized', () => {
  updateDocumentDirection(i18n.language);
});

// Initialize i18n
const initPromise = i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        translation: enTranslations
      },
      he: {
        translation: heTranslations
      }
    },
    fallbackLng: 'en',
    defaultNS: 'translation',
    interpolation: {
      escapeValue: false // React already escapes values
    },
    detection: {
      // CRITICAL: Order matters!
      // 1. userPreference - checks localStorage first, then will be updated from database after user loads
      // 2. locationBased - detects Israel timezone FIRST (before browser language)
      //    - If timezone is Asia/Jerusalem → returns 'he' (Israeli users see Hebrew even with English browser)
      //    - Then checks browser language as secondary indicator
      // 3. navigator - browser language as fallback (only if locationBased returns null)
      // 4. Fallback to 'en' if nothing matches
      // 
      // IMPORTANT: locationBased runs BEFORE navigator, so Israeli users with English browser
      // will still see Hebrew based on timezone detection
      order: ['userPreference', 'locationBased', 'navigator'],
      caches: ['localStorage'],
      // Cache language preference - but it will be overridden by database preference when user loads
      lookupLocalStorage: 'i18nextLng',
      lookupSessionStorage: 'i18nextLng',
      // Don't check cookie (we use localStorage)
      checkWhitelist: false
    }
  });

// Set initial direction after init completes
// This handles the race condition where i18n.language wasn't ready
initPromise.then(() => {
  // Set direction immediately after init
  const detectedLang = i18n.language || i18n.resolvedLanguage || 'en';
  updateDocumentDirection(detectedLang);
  
  // Also set it again after a short delay to ensure it's applied
  // (some browsers need a moment for the DOM to be ready)
  setTimeout(() => {
    const lang = i18n.language || i18n.resolvedLanguage || 'en';
    updateDocumentDirection(lang);
  }, 100);
  
  // One more check after a longer delay (for slow devices)
  setTimeout(() => {
    const lang = i18n.language || i18n.resolvedLanguage || 'en';
    updateDocumentDirection(lang);
  }, 500);
});

// Also try to set direction immediately if language is already known
// (helps when localStorage has the language cached)
const initialLang = i18n.language || i18n.resolvedLanguage;
if (initialLang) {
  updateDocumentDirection(initialLang);
}

// Force direction update on page load (for cases where i18n init happens after DOM ready)
if (typeof window !== 'undefined') {
  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => {
        if (i18n.isInitialized) {
          const lang = i18n.language || i18n.resolvedLanguage || 'en';
          updateDocumentDirection(lang);
        }
      }, 50);
    });
  } else {
    // DOM already ready
    setTimeout(() => {
      if (i18n.isInitialized) {
        const lang = i18n.language || i18n.resolvedLanguage || 'en';
        updateDocumentDirection(lang);
      }
    }, 50);
  }
}

export default i18n;


